/**
 * The docs table of contents.
 *
 * Kept as data rather than JSX so the sidebar, the mobile drawer and the
 * prev/next footer all read from one list, and so ordering is unit-testable.
 * `path` values must match the file-based route paths in src/routes/docs.*.tsx.
 */

export interface DocPageMeta {
  /** Route path, e.g. `/docs/double-entry`. `/docs` is the index. */
  path: string
  title: string
  /** One line shown under the title on the index page. */
  summary: string
}

export interface DocSection {
  label: string
  pages: DocPageMeta[]
}

export const DOCS_NAV: DocSection[] = [
  {
    label: 'Getting started',
    pages: [
      {
        path: '/docs',
        title: 'Start here',
        summary: 'What FolioWorth is, the one rule it enforces, and the first ten minutes.',
      },
    ],
  },
  {
    label: 'Concepts',
    pages: [
      {
        path: '/docs/double-entry',
        title: 'Double-entry, explained',
        summary: 'Why every entry sums to zero, and what the database does when one does not.',
      },
      {
        path: '/docs/accounts',
        title: 'Accounts',
        summary: 'Asset, Investment and Debt, the three system account types, and how net worth is computed.',
      },
      {
        path: '/docs/bills',
        title: 'Bills',
        summary: 'Payees, monthly instances, and the four amounts that are deliberately kept apart.',
      },
      {
        path: '/docs/reconciliation',
        title: 'Reconciliation',
        summary: '"I paid it" and "the bank took it" are different facts. Here is how they are tracked.',
      },
    ],
  },
  {
    label: 'Screens',
    pages: [
      {
        path: '/docs/payment-grid',
        title: 'Payment grid',
        summary: 'Payees down, months across. Reading it, and what each colour means.',
      },
      {
        path: '/docs/flow-map',
        title: 'Money flow map',
        summary: 'Accounts as nodes, ledger pairs as edges, saved views and layout modes.',
      },
      {
        path: '/docs/contribution-room',
        title: 'Contribution room',
        summary: 'TFSA, RRSP and FHSA room counted from actual ledger deposits, not memory.',
      },
      {
        path: '/docs/performance',
        title: 'Performance',
        summary: 'Time-weighted returns, XIRR, the method badge, and honest benchmarks.',
      },
      {
        path: '/docs/fire',
        title: 'FIRE planner',
        summary: 'Monte Carlo retirement simulation: assumptions in, a distribution out — not a prophecy.',
      },
    ],
  },
  {
    label: 'Automation',
    pages: [
      {
        path: '/docs/automating',
        title: 'Automating it',
        summary: 'Bank connections, detected bills, auto-reconcile and recurring transactions.',
      },
      {
        path: '/docs/investments',
        title: 'Investments and ACB',
        summary: 'Cost base pooled per taxpayer across institutions, and what CRA expects.',
      },
      {
        path: '/docs/importing',
        title: 'Importing brokerage data',
        summary: 'Wealthsimple and TD activity exports, symbol resolution, and safe re-imports.',
      },
      {
        path: '/docs/ai-insights',
        title: 'AI insights',
        summary: 'What is sent to the model, what is not, the quota, and bring-your-own-key.',
      },
    ],
  },
  {
    label: 'Reference',
    pages: [
      {
        path: '/docs/reference',
        title: 'API and exports',
        summary: 'The REST API, personal access tokens, and getting your data out.',
      },
    ],
  },
]

/** Every page in sidebar order — the order prev/next walks. */
export function flattenDocsNav(nav: DocSection[] = DOCS_NAV): DocPageMeta[] {
  return nav.flatMap((section) => section.pages)
}

export function findDocPage(path: string, nav: DocSection[] = DOCS_NAV): DocPageMeta | undefined {
  return flattenDocsNav(nav).find((page) => page.path === path)
}

/**
 * Neighbours of `path` in reading order, for the prev/next footer.
 *
 * Returns undefined on either side at the ends rather than wrapping: wrapping
 * from the last page back to the first implies the docs are a loop.
 */
export function docNeighbours(
  path: string,
  nav: DocSection[] = DOCS_NAV,
): { prev?: DocPageMeta; next?: DocPageMeta } {
  const pages = flattenDocsNav(nav)
  const i = pages.findIndex((page) => page.path === path)
  if (i === -1) return {}
  return { prev: pages[i - 1], next: pages[i + 1] }
}

/**
 * Whether `path` should render as the active sidebar item for `current`.
 *
 * Reason: `/docs` is a prefix of every other docs path, so a naive
 * startsWith would light up the index on every page.
 */
export function isDocPageActive(path: string, current: string): boolean {
  return path === current
}
